<template>
  <!-- Emoji for the composer, and for reactions. The full picker is
       searchable, by category, with the ones picked most recently first;
       the compact one (the apps' popup chat) is a single grid to scroll —
       the recent ones, then every other. Picking one keeps the picker open
       for another; Escape or a click elsewhere closes it. -->
  <div
    ref="root"
    class="sc-emoji"
    :class="{ 'sc-emoji--compact': compact }"
    role="dialog"
    aria-label="Emoji"
    @keydown.esc.stop.prevent="$emit('close')"
  >
    <template v-if="compact">
      <div class="sc-emoji__body sc-emoji__body--compact">
        <div class="sc-emoji__grid">
          <button
            v-for="emoji in everyEmoji"
            :key="emoji.char"
            type="button"
            class="sc-emoji__item"
            :title="emoji.words ? emoji.words.split(' ')[0] : ''"
            :aria-label="(emoji.words && emoji.words.split(' ')[0]) || emoji.char"
            @click="pick(emoji.char)"
          >{{ emoji.char }}</button>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="sc-emoji__search">
        <i class="pi pi-search" aria-hidden="true"></i>
        <input
          ref="search"
          v-model="query"
          type="search"
          placeholder="Search emoji"
          aria-label="Search emoji"
          autocomplete="off"
          spellcheck="false"
        />
      </div>
      <nav v-if="!searching" class="sc-emoji__tabs" aria-label="Emoji categories">
        <button
          v-for="section in sections"
          :key="section.key"
          type="button"
          class="sc-emoji__tab"
          :class="{ 'is-active': activeKey === section.key }"
          :title="section.label"
          :aria-label="section.label"
          @click="jumpTo(section.key)"
        >
          <i v-if="section.key === 'recent'" class="pi pi-clock" aria-hidden="true"></i>
          <span v-else aria-hidden="true">{{ section.icon }}</span>
        </button>
      </nav>
      <div ref="grid" class="sc-emoji__body" @scroll.passive="onScroll">
        <template v-if="searching">
          <div v-if="results.length" class="sc-emoji__grid">
            <button
              v-for="emoji in results"
              :key="emoji.char"
              type="button"
              class="sc-emoji__item"
              :title="emoji.words.split(' ')[0]"
              :aria-label="emoji.words.split(' ')[0] || emoji.char"
              @click="pick(emoji.char)"
            >{{ emoji.char }}</button>
          </div>
          <p v-else class="sc-emoji__empty">No emoji found for “{{ query.trim() }}”</p>
        </template>
        <template v-else>
          <section v-for="section in sections" :key="section.key" :ref="`section-${section.key}`" class="sc-emoji__section" :data-key="section.key">
            <h6 class="sc-emoji__label">{{ section.label }}</h6>
            <div class="sc-emoji__grid">
              <button
                v-for="emoji in section.emojis"
                :key="emoji.char"
                type="button"
                class="sc-emoji__item"
                :title="emoji.words ? emoji.words.split(' ')[0] : ''"
                :aria-label="(emoji.words && emoji.words.split(' ')[0]) || emoji.char"
                @click="pick(emoji.char)"
              >{{ emoji.char }}</button>
            </div>
          </section>
        </template>
      </div>
    </template>
  </div>
</template>
<script>
import { CHAT_EMOJI_CATEGORIES, CHAT_EMOJIS, recentChatEmoji, rememberChatEmoji, searchChatEmoji } from '@/utils/chat_emoji';

export default {
  name: 'ChatEmojiPicker',
  props: {
    // A single grid to scroll, without the search and the categories.
    compact: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['pick', 'close'],
  data() {
    return {
      query: '',
      recent: recentChatEmoji(),
      activeKey: null,
      isTouch: typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(pointer: coarse)').matches,
    };
  },
  computed: {
    searching() {
      return this.query.trim().length > 0;
    },
    results() {
      return searchChatEmoji(this.query);
    },
    sections() {
      const byChar = new Map(CHAT_EMOJIS.map((emoji) => [emoji.char, emoji]));
      const recent = this.recent.map((char) => byChar.get(char) || { char, words: '' });
      return [
        ...(recent.length ? [{ key: 'recent', label: 'Recently used', icon: '', emojis: recent }] : []),
        ...CHAT_EMOJI_CATEGORIES,
      ];
    },
    // The compact grid: the recent ones first, then every other once.
    everyEmoji() {
      const byChar = new Map(CHAT_EMOJIS.map((emoji) => [emoji.char, emoji]));
      const recent = this.recent.map((char) => byChar.get(char) || { char, words: '' });
      const shown = new Set(recent.map((emoji) => emoji.char));
      return [...recent, ...CHAT_EMOJIS.filter((emoji) => !shown.has(emoji.char))];
    },
  },
  methods: {
    pick(char) {
      this.$emit('pick', char);
      // The recent row only changes the next time the picker opens, so the
      // grid does not shift under the pointer.
      rememberChatEmoji(char);
    },
    jumpTo(key) {
      const section = this.$refs[`section-${key}`];
      const element = Array.isArray(section) ? section[0] : section;
      if (element && this.$refs.grid) {
        this.$refs.grid.scrollTop = element.offsetTop - this.$refs.grid.offsetTop;
        this.activeKey = key;
      }
    },
    // The tab of the category scrolled to lights up.
    onScroll() {
      const grid = this.$refs.grid;
      if (!grid) return;
      const top = grid.scrollTop + grid.offsetTop + 8;
      let current = this.sections[0]?.key || null;
      this.sections.forEach((section) => {
        const ref = this.$refs[`section-${section.key}`];
        const element = Array.isArray(ref) ? ref[0] : ref;
        if (element && element.offsetTop <= top) current = section.key;
      });
      this.activeKey = current;
    },
    onPointerDown(event) {
      if (this.$refs.root && !this.$refs.root.contains(event.target)) this.$emit('close');
    },
  },
  mounted() {
    this.activeKey = this.sections[0]?.key || null;
    // Wait for the click that opened it to finish before listening for one outside.
    this.listenTimer = window.setTimeout(() => document.addEventListener('pointerdown', this.onPointerDown, true), 0);
    if (!this.isTouch && !this.compact) this.$nextTick(() => this.$refs.search?.focus());
  },
  beforeUnmount() {
    window.clearTimeout(this.listenTimer);
    document.removeEventListener('pointerdown', this.onPointerDown, true);
  },
};
</script>
